/**
 * Backend Database Client
 * A pure backend API client that replaces Supabase for database operations
 */

import type {
  User,
  Session,
  AuthResponse,
  SessionResponse,
  SignInCredentials,
  SignUpCredentials,
  OAuthCredentials,
  ResetPasswordCredentials,
  UpdateUserCredentials,
} from '@/types/auth';

// ==================== Type Transformation Utilities ====================

class ResponseTransformer {
  /**
   * Transform backend response to match expected format
   */
  static transformResponse<T>(
    data: any,
    table: string,
    operation: string
  ): { data: T | T[] | null; error: any } {
    if (!data) {
      return { data: null, error: null };
    }

    // Handle error responses
    if (data.detail || data.error) {
      return {
        data: null,
        error: {
          message: data.detail || data.error || 'Unknown error',
          code: data.status_code || 'UNKNOWN',
          details: data.detail || data.error,
        },
      };
    }

    // Transform data based on table-specific rules
    const transformed = this.applyTableTransformations(data, table);

    // Ensure dates are properly formatted
    const withDates = this.transformDates(transformed);

    return { data: withDates as T, error: null };
  }

  /**
   * Apply table-specific transformations
   */
  private static applyTableTransformations(data: any, table: string): any {
    const isArray = Array.isArray(data);
    const items = isArray ? data : [data];

    const transformed = items.map((item) => {
      const base = { ...item };

      switch (table) {
        case 'profiles':
          return {
            ...base,
            email: base.email || null,
            full_name: base.full_name || null,
            first_name: base.first_name || null,
            last_name: base.last_name || null,
            biography: base.biography || null,
            avatar_url: base.avatar_url || null,
            referral_code_id: base.referral_code_id || null,
            referral_earnings_total: base.referral_earnings_total || null,
            referred_by: base.referred_by || null,
          };

        case 'sessions':
          return {
            ...base,
            audio_url: base.audio_url || '',
            category_id: base.category_id || '',
            title: base.title || '',
            user_id: base.user_id || '',
            session_type: base.session_type || 'standard',
            session_scope: base.session_scope || 'personal',
            is_favorite: base.is_favorite || false,
            duration: base.duration || null,
            generated_content: base.generated_content || null,
            practitioner_id: base.practitioner_id || null,
            client_id: base.client_id || null,
          };

        default:
          return base;
      }
    });

    return isArray ? transformed : transformed[0];
  }

  /**
   * Transform date strings to ISO format
   */
  private static transformDates(data: any): any {
    if (!data) return data;

    const dateFields = [
      'created_at',
      'updated_at',
      'deleted_at',
      'expires_at',
      'started_at',
      'completed_at',
      'last_accessed_at',
      'access_expires_at',
      'current_period_start',
      'current_period_end',
      'transaction_date',
      'paid_at',
      'payout_date',
    ];

    const transform = (obj: any): any => {
      if (Array.isArray(obj)) {
        return obj.map(transform);
      }

      if (obj && typeof obj === 'object') {
        const result: any = {};

        for (const [key, value] of Object.entries(obj)) {
          if (dateFields.includes(key) && value) {
            result[key] = new Date(value as string).toISOString();
          } else if (value && typeof value === 'object') {
            result[key] = transform(value);
          } else {
            result[key] = value;
          }
        }

        return result;
      }

      return obj;
    };

    return transform(data);
  }
}

// ==================== Query Builder ====================

class QueryBuilder<T> {
  private tableName: string;
  private operation: 'select' | 'insert' | 'update' | 'delete' | 'upsert' = 'select';
  private filters: any[] = [];
  private orderBy: any[] = [];
  private limitValue?: number;
  private offsetValue?: number;
  private columns?: string;
  private data?: any;
  private upsertOptions?: { onConflict?: string };
  private singleRow: boolean = false;
  private backendUrl: string;
  private getAuthToken: () => Promise<string | null>;

  constructor(
    tableName: string,
    config: {
      backendUrl: string;
      getAuthToken: () => Promise<string | null>;
    }
  ) {
    this.tableName = tableName;
    this.backendUrl = config.backendUrl;
    this.getAuthToken = config.getAuthToken;
  }

  select(columns: string = '*'): QueryBuilder<T> {
    if (this.operation === 'select') {
      this.operation = 'select';
    }
    this.columns = columns;
    return this;
  }

  insert(data: Partial<T> | Partial<T>[]): QueryBuilder<T> {
    this.operation = 'insert';
    this.data = data;
    return this;
  }

  update(data: Partial<T>): QueryBuilder<T> {
    this.operation = 'update';
    this.data = data;
    return this;
  }

  upsert(data: Partial<T> | Partial<T>[], options?: { onConflict?: string }): QueryBuilder<T> {
    this.operation = 'upsert';
    this.data = data;
    this.upsertOptions = options;
    return this;
  }

  delete(): QueryBuilder<T> {
    this.operation = 'delete';
    return this;
  }

  eq(column: string, value: any): QueryBuilder<T> {
    this.filters.push({ type: 'eq', column, value });
    return this;
  }

  neq(column: string, value: any): QueryBuilder<T> {
    this.filters.push({ type: 'neq', column, value });
    return this;
  }

  gt(column: string, value: any): QueryBuilder<T> {
    this.filters.push({ type: 'gt', column, value });
    return this;
  }

  gte(column: string, value: any): QueryBuilder<T> {
    this.filters.push({ type: 'gte', column, value });
    return this;
  }

  lt(column: string, value: any): QueryBuilder<T> {
    this.filters.push({ type: 'lt', column, value });
    return this;
  }

  lte(column: string, value: any): QueryBuilder<T> {
    this.filters.push({ type: 'lte', column, value });
    return this;
  }

  like(column: string, pattern: string): QueryBuilder<T> {
    this.filters.push({ type: 'like', column, pattern });
    return this;
  }

  ilike(column: string, pattern: string): QueryBuilder<T> {
    this.filters.push({ type: 'ilike', column, pattern });
    return this;
  }

  is(column: string, value: any): QueryBuilder<T> {
    this.filters.push({ type: 'is', column, value });
    return this;
  }

  in(column: string, values: any[]): QueryBuilder<T> {
    this.filters.push({ type: 'in', column, values });
    return this;
  }

  contains(column: string, value: any): QueryBuilder<T> {
    this.filters.push({ type: 'contains', column, value });
    return this;
  }

  containedBy(column: string, value: any): QueryBuilder<T> {
    this.filters.push({ type: 'contained_by', column, value });
    return this;
  }

  range(column: string, start: any, end: any): QueryBuilder<T> {
    this.filters.push({ type: 'range', column, start, end });
    return this;
  }

  overlaps(column: string, value: any): QueryBuilder<T> {
    this.filters.push({ type: 'overlaps', column, value });
    return this;
  }

  match(query: Record<string, any>): QueryBuilder<T> {
    Object.entries(query).forEach(([column, value]) => {
      this.filters.push({ type: 'eq', column, value });
    });
    return this;
  }

  not(column: string, operator: string, value: any): QueryBuilder<T> {
    this.filters.push({ type: 'not', column, operator, value });
    return this;
  }

  or(conditions: string): QueryBuilder<T> {
    this.filters.push({ type: 'or', conditions });
    return this;
  }

  filter(column: string, operator: string, value: any): QueryBuilder<T> {
    this.filters.push({ type: operator, column, value });
    return this;
  }

  limit(count: number): QueryBuilder<T> {
    this.limitValue = count;
    return this;
  }

  offset(count: number): QueryBuilder<T> {
    this.offsetValue = count;
    return this;
  }

  order(column: string, options?: { ascending?: boolean; nullsFirst?: boolean }): QueryBuilder<T> {
    this.orderBy.push({
      column,
      ascending: options?.ascending ?? true,
      nullsFirst: options?.nullsFirst,
    });
    return this;
  }

  single(): QueryBuilder<T> {
    this.singleRow = true;
    this.limitValue = 1;
    return this;
  }

  maybeSingle(): QueryBuilder<T> {
    this.singleRow = true;
    this.limitValue = 1;
    return this;
  }

  /**
   * Remove timezone info from ISO datetime strings
   */
  private sanitizeDatetimes(data: any): any {
    if (!data) return data;

    const dateFields = [
      'created_at',
      'updated_at',
      'deleted_at',
      'expires_at',
      'started_at',
      'completed_at',
      'last_accessed_at',
      'access_expires_at',
      'current_period_start',
      'current_period_end',
      'transaction_date',
      'paid_at',
      'payout_date',
      'referral_applied_at',
    ];

    const sanitize = (obj: any): any => {
      if (Array.isArray(obj)) {
        return obj.map(sanitize);
      }

      if (obj && typeof obj === 'object') {
        const result: any = {};

        for (const [key, value] of Object.entries(obj)) {
          if (dateFields.includes(key) && value && typeof value === 'string') {
            result[key] = value.replace(/Z$/, '').replace(/[+-]\d{2}:\d{2}$/, '');
          } else if (value && typeof value === 'object') {
            result[key] = sanitize(value);
          } else {
            result[key] = value;
          }
        }

        return result;
      }

      return obj;
    };

    return sanitize(data);
  }

  async execute(): Promise<{ data: T | T[] | null; error: any }> {
    const endpoint = `${this.backendUrl}/db/${this.tableName}`;
    const authToken = await this.getAuthToken();

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    let url = endpoint;
    let method = 'GET';
    let body: any = undefined;

    const queryParams = new URLSearchParams();

    switch (this.operation) {
      case 'select':
        method = 'GET';
        if (this.columns && this.columns !== '*') {
          queryParams.append('select', this.columns);
        }
        if (this.filters.length > 0) {
          queryParams.append('filters', JSON.stringify(this.filters));
        }
        if (this.orderBy.length > 0) {
          queryParams.append('order', JSON.stringify(this.orderBy));
        }
        if (this.limitValue !== undefined) {
          queryParams.append('limit', this.limitValue.toString());
        }
        if (this.offsetValue !== undefined) {
          queryParams.append('offset', this.offsetValue.toString());
        }
        if (this.singleRow) {
          queryParams.append('single', 'true');
        }
        if (queryParams.toString()) {
          url += `?${queryParams.toString()}`;
        }
        break;

      case 'insert':
        method = 'POST';
        const sanitizedInsertData = this.sanitizeDatetimes(this.data);
        body = JSON.stringify(sanitizedInsertData);
        if (this.columns) {
          queryParams.append('select', this.columns);
        }
        if (this.singleRow) {
          queryParams.append('single', 'true');
        }
        if (queryParams.toString()) {
          url += `?${queryParams.toString()}`;
        }
        break;

      case 'update':
        method = 'PATCH';
        if (this.filters.length > 0) {
          queryParams.append('filters', JSON.stringify(this.filters));
        }
        if (this.columns) {
          queryParams.append('select', this.columns);
        }
        if (queryParams.toString()) {
          url += `?${queryParams.toString()}`;
        }
        const sanitizedUpdateData = this.sanitizeDatetimes(this.data);
        body = JSON.stringify(sanitizedUpdateData);
        break;

      case 'upsert':
        method = 'PUT';
        const sanitizedUpsertData = this.sanitizeDatetimes(this.data);
        body = JSON.stringify({
          data: sanitizedUpsertData,
          onConflict: this.upsertOptions?.onConflict,
        });
        if (this.columns) {
          queryParams.append('select', this.columns);
        }
        if (queryParams.toString()) {
          url += `?${queryParams.toString()}`;
        }
        break;

      case 'delete':
        method = 'DELETE';
        if (this.filters.length > 0) {
          queryParams.append('filters', JSON.stringify(this.filters));
        }
        if (this.columns) {
          queryParams.append('select', this.columns);
        }
        if (queryParams.toString()) {
          url += `?${queryParams.toString()}`;
        }
        break;
    }

    try {
      const response = await fetch(url, {
        method,
        headers,
        body,
        credentials: 'include',
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({
          message: response.statusText,
          code: response.status.toString(),
          details: response.statusText,
        }));
        return { data: null, error };
      }

      const data = await response.json();

      // Apply response transformation
      const transformed = ResponseTransformer.transformResponse<T>(data, this.tableName, this.operation);

      // Handle single row expectation
      if (this.singleRow && Array.isArray(transformed.data)) {
        const result = transformed.data[0] || null;
        return {
          data: result,
          error: transformed.error,
        };
      }

      return transformed;
    } catch (error: any) {
      return {
        data: null,
        error: {
          message: error.message || 'Network error',
          code: 'NETWORK_ERROR',
          details: error.toString(),
        },
      };
    }
  }

  then<TResult>(
    onfulfilled?: (value: { data: T | T[] | null; error: any }) => TResult | PromiseLike<TResult>
  ): Promise<TResult> {
    return this.execute().then(onfulfilled as any);
  }

  catch(onrejected?: (reason: any) => any): Promise<{ data: T | T[] | null; error: any }> {
    return this.execute().catch(onrejected);
  }
}

// ==================== Auth Client ====================

class BackendAuth {
  private backendUrl: string;
  private getAuthToken: () => Promise<string | null>;

  constructor(backendUrl: string, getAuthToken: () => Promise<string | null>) {
    this.backendUrl = backendUrl;
    this.getAuthToken = getAuthToken;
  }

  async signInWithPassword(credentials: SignInCredentials): Promise<AuthResponse> {
    const formData = new FormData();
    formData.append('username', credentials.email);
    formData.append('password', credentials.password);

    const response = await fetch(`${this.backendUrl}/auth/login`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      return { data: undefined, error: { message: error.detail || 'Login failed' } };
    }

    const data = await response.json();

    // Store tokens
    localStorage.setItem('auth_token', data.access_token);
    if (data.refresh_token) {
      localStorage.setItem('refresh_token', data.refresh_token);
    }

    // Fetch user profile
    const userResponse = await fetch(`${this.backendUrl}/auth/me`, {
      headers: { Authorization: `Bearer ${data.access_token}` },
      credentials: 'include',
    });

    const user = userResponse.ok ? await userResponse.json() : null;

    return {
      data: {
        user,
        session: {
          access_token: data.access_token,
          refresh_token: data.refresh_token,
          user,
        },
      },
      error: undefined,
    };
  }

  async signUp(credentials: SignUpCredentials): Promise<AuthResponse> {
    const response = await fetch(`${this.backendUrl}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        email: credentials.email,
        password: credentials.password,
        first_name: credentials.options?.data?.first_name || '',
        last_name: credentials.options?.data?.last_name || '',
        full_name: credentials.options?.data?.full_name || '',
        biography: credentials.options?.data?.biography || '',
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      return { data: undefined, error: { message: error.detail || 'Registration failed' } };
    }

    const data = await response.json();

    return {
      data: {
        user: { id: data.user_id, email: credentials.email },
        session: { access_token: data.access_token, user: { id: data.user_id, email: credentials.email } },
      },
      error: undefined,
    };
  }

  /**
   * Step 1 of OTP signup: Initiate email verification
   * Sends a 6-digit OTP to the user's email
   */
  async initiateSignUp(credentials: SignUpCredentials): Promise<{
    data?: { message: string; email: string; expires_in: number };
    error?: { message: string };
  }> {
    const response = await fetch(`${this.backendUrl}/auth/signup/initiate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        email: credentials.email,
        password: credentials.password,
        first_name: credentials.options?.data?.first_name || '',
        last_name: credentials.options?.data?.last_name || '',
        full_name: credentials.options?.data?.full_name || '',
        referral_code_id: credentials.options?.data?.referral_code_id || null,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      return { error: { message: error.detail || 'Failed to send verification code' } };
    }

    const data = await response.json();
    return { data };
  }

  /**
   * Step 2 of OTP signup: Verify OTP and create account
   * Validates the OTP and creates the user account
   */
  async verifySignUpOTP(
    email: string,
    otp: string
  ): Promise<AuthResponse> {
    const response = await fetch(`${this.backendUrl}/auth/signup/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, otp }),
    });

    if (!response.ok) {
      const error = await response.json();
      return { data: undefined, error: { message: error.detail || 'Verification failed' } };
    }

    const data = await response.json();

    // Fetch user profile after successful verification
    const userResponse = await fetch(`${this.backendUrl}/auth/me`, {
      credentials: 'include',
    });

    const user = userResponse.ok ? await userResponse.json() : { id: data.user_id, email };

    return {
      data: {
        user,
        session: { user },
      },
      error: undefined,
    };
  }

  /**
   * Resend OTP for pending signup verification
   */
  async resendSignUpOTP(email: string): Promise<{
    data?: { message: string; expires_in: number };
    error?: { message: string };
  }> {
    const response = await fetch(`${this.backendUrl}/auth/signup/resend-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      const error = await response.json();
      return { error: { message: error.detail || 'Failed to resend verification code' } };
    }

    const data = await response.json();
    return { data };
  }

  async signInWithOAuth(credentials: OAuthCredentials): Promise<{ error: any }> {
    const params = new URLSearchParams({
      redirect_uri: credentials.options?.redirectTo || window.location.origin,
      state: window.location.pathname,
    });

    window.location.href = `${this.backendUrl}/auth/${credentials.provider}?${params}`;
    return { error: null };
  }

  async signOut(): Promise<{ error: any }> {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user_data');
    return { error: null };
  }

  async getSession(): Promise<SessionResponse> {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      return { data: { session: null }, error: undefined };
    }

    const response = await fetch(`${this.backendUrl}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
      credentials: 'include',
    });

    if (!response.ok) {
      localStorage.removeItem('auth_token');
      return { data: { session: null }, error: undefined };
    }

    const user = await response.json();
    return {
      data: {
        session: {
          access_token: token,
          user,
        },
      },
      error: undefined,
    };
  }

  async getUser() {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      return { data: { user: null }, error: null };
    }

    const response = await fetch(`${this.backendUrl}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
      credentials: 'include',
    });

    if (!response.ok) {
      localStorage.removeItem('auth_token');
      return { data: { user: null }, error: null };
    }

    const user = await response.json();
    return {
      data: { user },
      error: null,
    };
  }

  async resetPasswordForEmail(email: string, options?: { redirectTo?: string }) {
    const response = await fetch(`${this.backendUrl}/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        email,
        redirect_url: options?.redirectTo,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      return { error: { message: error.detail || 'Password reset failed' } };
    }

    return { error: null };
  }

  async updateUser(updates: UpdateUserCredentials) {
    const token = localStorage.getItem('auth_token');

    if (updates.password) {
      const response = await fetch(`${this.backendUrl}/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'include',
        body: JSON.stringify({ new_password: updates.password }),
      });

      if (!response.ok) {
        const error = await response.json();
        return { error: { message: error.detail || 'Password update failed' } };
      }
    }

    return { data: {}, error: null };
  }

  onAuthStateChange(callback: (event: string, session: Session | null) => void) {
    let lastToken = localStorage.getItem('auth_token');

    const checkAuth = setInterval(async () => {
      const currentToken = localStorage.getItem('auth_token');

      if (currentToken !== lastToken) {
        if (currentToken && !lastToken) {
          const { data } = await this.getSession();
          callback('SIGNED_IN', data?.session || null);
        } else if (!currentToken && lastToken) {
          callback('SIGNED_OUT', null);
        }
        lastToken = currentToken;
      }
    }, 1000);

    return {
      data: { subscription: { unsubscribe: () => clearInterval(checkAuth) } },
    };
  }
}

// ==================== Storage Client ====================

class BackendStorage {
  constructor(
    private backendUrl: string,
    private getAuthToken: () => Promise<string | null>
  ) {}

  from(bucket: string) {
    return {
      getPublicUrl: (path: string) => ({
        data: { publicUrl: `${this.backendUrl}/storage/${bucket}/${path}` },
      }),

      upload: async (path: string, file: File | Blob | ArrayBuffer, options?: any) => {
        const formData = new FormData();
        if (file instanceof File || file instanceof Blob) {
          formData.append('file', file);
        } else {
          formData.append('file', new Blob([file]));
        }

        const token = await this.getAuthToken();
        const response = await fetch(`${this.backendUrl}/storage/${bucket}/${path}`, {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          credentials: 'include',
          body: formData,
        });

        if (!response.ok) {
          return { data: null, error: await response.json() };
        }
        return { data: await response.json(), error: null };
      },

      download: async (path: string) => {
        const token = await this.getAuthToken();
        const response = await fetch(`${this.backendUrl}/storage/${bucket}/${path}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          credentials: 'include',
        });

        if (!response.ok) {
          return { data: null, error: await response.json() };
        }
        return { data: await response.blob(), error: null };
      },

      remove: async (paths: string[]) => {
        const token = await this.getAuthToken();
        const response = await fetch(`${this.backendUrl}/storage/${bucket}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          credentials: 'include',
          body: JSON.stringify({ paths }),
        });

        if (!response.ok) {
          return { data: null, error: await response.json() };
        }
        return { data: await response.json(), error: null };
      },

      list: async (path?: string, options?: any) => {
        const token = await this.getAuthToken();
        const params = new URLSearchParams();
        if (path) params.append('path', path);
        if (options?.limit) params.append('limit', options.limit.toString());
        if (options?.offset) params.append('offset', options.offset.toString());

        const response = await fetch(`${this.backendUrl}/storage/${bucket}/list?${params}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          credentials: 'include',
        });

        if (!response.ok) {
          return { data: null, error: await response.json() };
        }
        return { data: await response.json(), error: null };
      },
    };
  }
}

// ==================== Functions Client ====================

class BackendFunctions {
  constructor(
    private backendUrl: string,
    private getAuthToken: () => Promise<string | null>
  ) {}

  async invoke(functionName: string, options?: { body?: any }) {
    const token = await this.getAuthToken();
    const response = await fetch(`${this.backendUrl}/functions/${functionName}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      credentials: 'include',
      body: JSON.stringify(options?.body || {}),
    });

    if (!response.ok) {
      return { data: null, error: await response.json() };
    }

    return { data: await response.json(), error: null };
  }
}

// ==================== Main Backend Client ====================

export class BackendClient {
  public auth: BackendAuth;
  public storage: BackendStorage;
  public functions: BackendFunctions;
  private backendUrl: string;

  constructor(backendUrl?: string) {
    this.backendUrl = backendUrl || import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000/api/v1';
    this.auth = new BackendAuth(this.backendUrl, () => this.getAuthToken());
    this.storage = new BackendStorage(this.backendUrl, () => this.getAuthToken());
    this.functions = new BackendFunctions(this.backendUrl, () => this.getAuthToken());
  }

  private async getAuthToken(): Promise<string | null> {
    return localStorage.getItem('auth_token');
  }

  from<T = any>(table: string): QueryBuilder<T> {
    return new QueryBuilder<T>(table, {
      backendUrl: this.backendUrl,
      getAuthToken: () => this.getAuthToken(),
    });
  }

  async rpc(fn: string, args?: any) {
    const token = await this.getAuthToken();
    const response = await fetch(`${this.backendUrl}/db/rpc/${fn}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      credentials: 'include',
      body: JSON.stringify(args || {}),
    });

    if (!response.ok) {
      const error = await response.json();
      throw error;
    }

    const data = await response.json();
    return ResponseTransformer.transformResponse(data, fn, 'rpc').data;
  }
}

// Export singleton instance
export const backendClient = new BackendClient();
